import { DISALLOWED_EMAILS, ERROR_MSGS, MAX_FILE_SIZE, MAX_PDF_SIZE, TLD_S3_PATH_CONFIG, TLDS } from '../constants'
import { FILE_TYPE_PDF } from '../components/SelectedFile'
import { PLANS_ID } from '../constants/plans'
import JSZip from 'jszip'

export function isPDF(file) {
  return file.type === FILE_TYPE_PDF
}

const convertToBytes = (size) => 1024 * 1024 * size

export function formatMbToGb({ mb, pow = 1 }) {
  const k = 1024
  return Number(Number(mb / Math.pow(k, pow)).toFixed(4))
}

export function isValidFileSize(file, maxSize) {
  if (maxSize) {
    return file.size < convertToBytes(maxSize)
  } else if (file.type === FILE_TYPE_PDF) {
    return file.size < convertToBytes(MAX_PDF_SIZE)
  } else return file.size < convertToBytes(MAX_FILE_SIZE)
}

export function isValidFileSizeV2(initialFile, filesArray, maxSize) {
  const totalSize = filesArray.reduce((accumulator, file) => accumulator + file.size, initialFile.size)
  if (maxSize) {
    return totalSize < convertToBytes(maxSize)
  }
  const maxFileSize = initialFile.type === FILE_TYPE_PDF ? MAX_PDF_SIZE : MAX_FILE_SIZE
  return totalSize < convertToBytes(maxFileSize)
}

export const pluralize = (singular, plural, count) => (count === 1 ? singular : plural)

export function formatNumber(n) {
  const nFormat = new Intl.NumberFormat()
  return nFormat.format(n)
}

export const copyToClipboard = (text) => {
  const el = document.createElement('textarea')
  el.value = text
  document.body.appendChild(el)
  el.select()
  document.execCommand('copy')
  document.body.removeChild(el)
}

export const getDirectFileLink = (root, filename, ext, dateCreated) => {
  if (!filename) return null
  else return `${root}/${filename.replace(`.${ext}`, '')}-${dateCreated.replace(/:|\\./g, '-')}.${ext}`
}

export const validateDisallowedEmails = (email) =>
  !DISALLOWED_EMAILS.some((e) => email.match(`${e}$`)) && !email.includes('+')

export const getErrorMessage = (err) => {
  if (err && err.response && err.response.data && err.response.data.code) {
    return ERROR_MSGS[err.response.data.code]
  }
  return undefined
}

export function isDevSite() {
  return window.location.hostname.includes('.tiiny.co.uk') || window.location.hostname === 'localhost'
}

export const mbValue = (mb) => 1024 * 1024 * mb

export const isTrialAccount = ({ productId }) => !productId

export const isMac = () => navigator?.platform?.toUpperCase().indexOf('MAC') > -1

export function hashString(str = '') {
  let hash = 0,
    i,
    chr
  if (str?.length === 0) return hash
  for (i = 0; i < str.length; i++) {
    chr = str.charCodeAt(i)
    hash = (hash << 5) - hash + chr
    hash |= 0 // Convert to 32bit integer
  }
  return hash
}

export const getTLDs = ({ account }) => {
  if (isDevSite()) return [TLDS.TIINY_CO_UK]
  return account?.validated ? [TLDS.TIINY_CO, TLDS.TIINY_SITE] : [TLDS.TIINY_SITE]
}

export const getSubdomainAndTLD = ({ site }) => {
  const { subdomain } = site
  const splitSubdomain = subdomain?.split('.')

  return splitSubdomain ? { subdomain: splitSubdomain[0], tld: `.${splitSubdomain.slice(1).join('.')}` } : {}
}

export const getS3Path = ({ site, isCustomDomain }) => {
  const { subdomain, tld } = getSubdomainAndTLD({ site })
  return isCustomDomain ? `${tld.substring(1)}/${subdomain}` : TLD_S3_PATH_CONFIG[tld]({ subdomain, tld })
}

export const hasFeature = (planId, feature) => PLANS_ID[planId]?.features[feature]

// Helper function to create a file input element
export const createFileInput = () => {
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = '*/*'
  return input
}

// Helper function to check if a file is valid based on type and size
export const isValidFile = (fileSelected, selectedFile, files, maxSizeMB) => {
  const isPdfFile = fileSelected.type === 'application/pdf'
  const isHtmlFile = fileSelected.type === 'text/html'
  const isPdfSelected = selectedFile.type === 'application/pdf'

  if (isHtmlFile || (isPdfFile && isPdfSelected)) {
    const updatedFiles = [...files, selectedFile]
    if (maxSizeMB) {
      return isValidFileSizeV2(fileSelected, updatedFiles, maxSizeMB)
    } else {
      return isValidFileSizeV2(fileSelected, updatedFiles, maxSizeMB)
    }
  } else {
    alert('Only pdf files can be added. To add other files, please delete all the files.')
    return false
  }
}

export async function zipMultipleFiles(addedFiles) {
  const zip = new JSZip()
  addedFiles.forEach((file) => {
    zip.file(file.path, file)
  })
  const content = await zip.generateAsync({ type: 'blob' })

  return new File([content], 'files.zip', {
    type: 'application/zip',
    lastModified: new Date()
  })
}

export const getUpdatedFields = (values, initialValues) => {
  let newValues = values

  const updatedFields = Object.keys(newValues).reduce((acc, key) => {
    if (values[key] !== initialValues[key]) {
      if (key === 'subdomain' || key === 'domainSuffix') {
        acc['subdomain'] = values['subdomain']
        acc['domainSuffix'] = values['domainSuffix']
      } else if (key === 'disableExportPdf' || key === 'displayMode') {
        acc['disableExportPdf'] = values['disableExportPdf']
        acc['displayMode'] = values['displayMode']
      } else {
        acc[key] = values[key]
      }
    }
    return acc
  }, {})

  return Object.keys(updatedFields).length === 0 ? null : updatedFields
}

export const getFullDomainValue = (subdomain = '', domainSuffix = '') => {
  return subdomain + domainSuffix
}

export const getDomainSuffix = (subdomain) => {
  const splitSubdomain = subdomain?.split('.')

  return subdomain ? { subdomain: splitSubdomain[0], tld: `.${splitSubdomain.slice(1).join('.')}` } : {}
}

export const convertToFormData = (formData, key, value) => {
  if (value !== null && value !== undefined) {
    formData.append(key, value)
  }
}

export const formDataToJson = (formData) => {
  const jsonObject = {}
  for (const [key, value] of formData.entries()) {
    jsonObject[key] = value
  }
  return jsonObject
}
